import {
  ANALYTICS_JLDATA_UPDATED,
  CHUNK_LOADED,
  PAGE_LOAD,
} from "@/utils/events";
import { useEffect, useRef, useState } from "react";
import { setJLData } from "@/utils/setJLData";
import { useFeatureFlags } from "@/flags/flags";
import { RouteParams, useRouteParams } from "@/hooks/useRouteParams";
import { useRouter } from "next/router";
import { useWindowSize } from "@/hooks/useWindowSize";
import { analyticsLogger } from "@/utils/clientLogger";
import { isAlsoSearched, setAlsoSearched } from "@/utils/alsoSearched";
import { dispatchAnalyticsEvent } from "@~/utils/analytics";
import { mapProduct } from "@/utils/mapProduct";
import { ProductListingData } from "@/types/productListing";
import { PageProps } from "@/getServerSideProps/getServerSideProps";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";
import { setCustomAttribute } from "@/utils/newrelic";

export function Analytics({
  productListingData,
  pageConfig,
}: Pick<PageProps, "productListingData" | "pageConfig">) {
  const flags = useFeatureFlags();
  const route = useRouteParams();
  const router = useRouter();
  const [initialPageLoad, setInitialPageLoad] = useState(true);
  const { width: windowWidth } = useWindowSize();
  const pageSize = Number(flags.pageSize.value);
  const chunkSize = Number(flags.chunkSize.value);

  const { previousProductListingData } = usePreviousData(
    productListingData,
    route,
  );

  useEffect(() => {
    if (productListingData !== previousProductListingData) {
      analyticsLogger.info("Analytics: useEffect: updating jlData...");
      const jlData = setJLData({
        productListingData,
        flags,
        route,
        router,
        windowWidth: windowWidth || 0,
        hasUserClickedSimilarSearch: isAlsoSearched(),
      });
      analyticsLogger.info("Analytics: useEffect: jlData", jlData);
      analyticsLogger.info(
        `Analytics: useEffect: dispatching ${ANALYTICS_JLDATA_UPDATED} event`,
      );
      document.body.dispatchEvent(new CustomEvent(ANALYTICS_JLDATA_UPDATED));

      setAlsoSearched(false);

      if (initialPageLoad) {
        dispatchAnalyticsEvent(PAGE_LOAD);
        setCustomAttribute("pageConfig", pageConfig.name);
        setInitialPageLoad(false);
      }
    }
  }, [
    productListingData,
    flags,
    route,
    router,
    windowWidth,
    previousProductListingData,
    initialPageLoad,
    pageConfig.name,
  ]);

  useEffect(() => {
    if (isChunkLoad(previousProductListingData, productListingData)) {
      dispatchChunkLoadedAnalyticsEvent({
        route,
        pageSize,
        chunkSize,
        productListingData,
      });
    }
  }, [
    productListingData,
    previousProductListingData,
    route,
    pageSize,
    chunkSize,
  ]);

  return null;
}

const dispatchChunkLoadedAnalyticsEvent = ({
  route,
  pageSize,
  chunkSize,
  productListingData,
}: {
  route: RouteParams;
  pageSize: number;
  chunkSize: number;
  productListingData: ProductListingData;
}) => {
  const chunk = Number(route.query.chunk || 1);
  const page = Number(route.query.page || 1);
  const productOffset = productListingData.products.length - chunkSize;

  sendMonetateEvent("plp.chunkLoaded");

  dispatchAnalyticsEvent(CHUNK_LOADED, {
    chunkDetails: {
      chunkOffset: chunk - 1,
      productOffset: pageSize * (page - 1) + chunkSize * (chunk - 1),
      vertexAttributionToken: productListingData.analytics?.attributionToken,
      products: productListingData.products
        .slice(chunkSize * -1)
        .map((product, index) => mapProduct(product, index + productOffset)),
      personalisedPlp: productListingData.isPersonalised,
    },
  });
};

const usePreviousData = (
  productListingData: ProductListingData,
  route: RouteParams,
) => {
  const productListingDataRef = useRef<ProductListingData | undefined>(
    undefined,
  );
  const routeRef = useRef<RouteParams | undefined>(undefined);

  useEffect(() => {
    if (productListingDataRef.current !== productListingData) {
      productListingDataRef.current = productListingData;
      routeRef.current = route;
    }
  }, [productListingData, route]);

  return {
    previousProductListingData: productListingDataRef.current,
    previousRoute: routeRef.current,
  };
};

const isChunkLoad = (
  previousProductListingData: ProductListingData | undefined,
  currentProductListingData: ProductListingData,
) => {
  const prevProducts = previousProductListingData?.products;
  const currentProducts = currentProductListingData.products;
  if (
    !prevProducts ||
    prevProducts.length === 0 ||
    prevProducts.length >= currentProducts.length
  ) {
    return false;
  }

  return currentProducts
    .slice(0, prevProducts.length)
    .every((currProduct) =>
      prevProducts.find(({ productId }) => productId === currProduct.productId),
    );
};
